import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import { 
  CalendarDays, Clock, Send, Loader2, ArrowLeft,
  Calendar as CalendarIcon, FileText, Trash2, Building2, QrCode
} from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StaffLeavePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // URL params
  const companyCodeParam = searchParams.get('company');
  
  // State
  const [companyCode, setCompanyCode] = useState(companyCodeParam || localStorage.getItem('prc_company_code') || '');
  const [employee, setEmployee] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // QR Scanner state
  const [qrScanning, setQrScanning] = useState(false);
  const qrScannerRef = useRef(null);
  
  // Form state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [halfDay, setHalfDay] = useState(null);
  const [reason, setReason] = useState('');
  const [showForm, setShowForm] = useState(false);
  
  // Company info
  const [companyInfo, setCompanyInfo] = useState(null);

  // Verify company code on load if present
  useEffect(() => {
    if (companyCode && companyCode.length >= 4) {
      verifyCompany();
    }
  }, []);

  // Start QR scanner when company is verified
  useEffect(() => {
    let mounted = true;
    
    if (companyInfo && !employee) {
      const timer = setTimeout(() => {
        if (mounted && !qrScannerRef.current) {
          startQrScanner();
        }
      }, 500);
      
      return () => {
        mounted = false;
        clearTimeout(timer);
        stopQrScanner();
      };
    }
    
    return () => {
      mounted = false;
      stopQrScanner();
    };
  }, [companyInfo, employee]);

  const verifyCompany = async () => {
    try {
      const response = await axios.get(`${API}/api/verify-company?company_code=${companyCode}`);
      if (response.data.company) {
        setCompanyInfo(response.data.company);
        localStorage.setItem('prc_company_code', companyCode);
      }
    } catch (error) {
      console.error('Company verification failed:', error);
      toast.error('企業コードが無効です');
    }
  };

  // Start QR Scanner
  const startQrScanner = async () => {
    if (qrScanning || qrScannerRef.current) {
      return;
    }
    
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      
      const qrReaderElement = document.getElementById('qr-reader-leave');
      if (!qrReaderElement) {
        return;
      }
      
      qrReaderElement.innerHTML = '';
      setQrScanning(true);
      
      const scanner = new Html5Qrcode('qr-reader-leave');
      qrScannerRef.current = scanner;
      
      await scanner.start(
        { facingMode: "environment" },
        { fps: 15, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          // Vibration feedback
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          
          // Play success sound
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = 1200;
            oscillator.type = 'sine';
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.15);
          } catch (e) {}
          
          await handleQrScanned(decodedText);
        },
        () => {}
      );
    } catch (err) {
      console.error('QR Scanner error:', err);
      setQrScanning(false);
      qrScannerRef.current = null;
    }
  };

  // Stop QR Scanner
  const stopQrScanner = async () => {
    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.stop();
      } catch (e) {}
      qrScannerRef.current = null;
    }
    setQrScanning(false);
  };

  // Handle QR code scanned
  const handleQrScanned = async (qrData) => {
    try {
      // Parse QR data (format: company_id:employee_id:employee_code)
      const parts = qrData.split(':');
      if (parts.length < 2) {
        toast.error('無効なQRコードです');
        return;
      }
      
      const employeeId = parts[1];
      
      // Stop scanner
      await stopQrScanner();
      
      // Load employee data
      setLoading(true);
      const reqRes = await axios.get(`${API}/api/staff/leave/my-requests/${employeeId}?company_code=${companyCode}`);
      setEmployee(reqRes.data.employee);
      setRequests(reqRes.data.requests);
      
      toast.success(`${reqRes.data.employee.name}さん、ようこそ`);
      
    } catch (error) {
      console.error('QR auth failed:', error);
      toast.error(error.response?.data?.detail || '認証に失敗しました');
      // Restart scanner on error
      setTimeout(() => startQrScanner(), 1000);
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    setEmployee(null);
    setRequests([]);
    setShowForm(false);
    setTimeout(() => startQrScanner(), 500);
  };

  // Calculate days
  const calculateDays = () => {
    if (!startDate) return 0;
    if (halfDay) return 0.5;
    if (!endDate) return 1;
    return differenceInDays(endDate, startDate) + 1;
  };

  // Submit leave request
  const submitRequest = async () => {
    if (!startDate) {
      toast.error('開始日を選択してください');
      return;
    }
    
    const days = calculateDays();
    if (days > (employee?.paid_leave_balance || 0)) {
      toast.error(`有給残日数が不足しています（残り: ${employee?.paid_leave_balance || 0}日）`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/staff/leave/request`, {
        company_code: companyCode,
        employee_id: employee.id,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate || startDate, 'yyyy-MM-dd'),
        half_day: halfDay,
        reason: reason || undefined
      });
      
      toast.success('有給申請を送信しました');
      
      // Reload requests
      const reqRes = await axios.get(`${API}/api/staff/leave/my-requests/${employee.id}?company_code=${companyCode}`);
      setEmployee(reqRes.data.employee);
      setRequests(reqRes.data.requests);
      
      // Reset form
      setStartDate(null);
      setEndDate(null);
      setHalfDay(null);
      setReason('');
      setShowForm(false);
      
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.response?.data?.detail || '申請に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel request
  const cancelRequest = async (requestId) => {
    if (!window.confirm('この申請を取り消しますか？')) return;
    
    try {
      await axios.delete(`${API}/api/staff/leave/request/${requestId}?company_code=${companyCode}&employee_id=${employee.id}`);
      toast.success('申請を取り消しました');
      
      // Reload requests
      const reqRes = await axios.get(`${API}/api/staff/leave/my-requests/${employee.id}?company_code=${companyCode}`);
      setEmployee(reqRes.data.employee);
      setRequests(reqRes.data.requests);
      
    } catch (error) {
      console.error('Cancel failed:', error);
      toast.error(error.response?.data?.detail || '取り消しに失敗しました');
    }
  };

  // Status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400">申請中</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">承認済</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">却下</span>;
      default:
        return null;
    }
  };

  // Company code input screen
  if (!companyInfo) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <main className="max-w-md mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarDays className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">有給申請</h1>
            <p className="text-slate-400 text-sm">従業員セルフサービス</p>
          </div>
          
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6 space-y-4">
              <div>
                <Label className="text-slate-300">企業コード</Label>
                <Input
                  value={companyCode}
                  onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                  placeholder="例: PRC6271"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              
              <Button 
                onClick={verifyCompany}
                disabled={!companyCode}
                className="w-full bg-emerald-600 hover:bg-emerald-500"
              >
                次へ
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // QR Login screen
  if (!employee) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <main className="max-w-md mx-auto px-4 py-8">
          {/* Company badge */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full">
              <Building2 className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium">{companyInfo.name}</span>
            </div>
          </div>
          
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-center mb-4">QRコードでログイン</h2>
              
              {/* QR Scanner */}
              <div className="relative mb-4">
                <div id="qr-reader-leave" className="w-full rounded-lg overflow-hidden bg-black" style={{ minHeight: '280px' }}></div>
                
                {/* Scanner overlay */}
                {qrScanning && !loading && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36">
                      <div 
                        className="absolute inset-0 border border-emerald-400/60 rounded"
                        style={{ boxShadow: '0 0 15px 2px rgba(52, 211, 153, 0.3), inset 0 0 15px 2px rgba(52, 211, 153, 0.1)' }}
                      ></div>
                      <div className="absolute -top-1 -left-1 w-6 h-6 border-l-2 border-t-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 border-r-2 border-t-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                      <div className="absolute -bottom-1 -left-1 w-6 h-6 border-l-2 border-b-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 border-r-2 border-b-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                      <div 
                        className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
                        style={{
                          animation: 'scanLine 2s ease-in-out infinite',
                          boxShadow: '0 0 10px 2px rgba(52, 211, 153, 0.6)'
                        }}
                      ></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div style={{ animation: 'futurePulse 2s ease-in-out infinite' }}>
                          <QrCode className="w-10 h-10 text-emerald-400/40" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                    <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
                  </div>
                )}
                
                {!qrScanning && !loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                    <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
                    <span className="ml-3 text-white">カメラ起動中...</span>
                  </div>
                )}
              </div>
              
              <p className="text-center text-slate-400 text-xs">
                従業員のQRコードをスキャンしてください
              </p>
            </CardContent>
          </Card>
          
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                stopQrScanner();
                setCompanyInfo(null);
              }}
              className="w-full border-slate-600 text-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              戻る
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Main screen (after login)
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={handleLogout} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-bold">有給申請</h1>
          <div className="w-5"></div>
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Employee Info & Balance */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold">{employee.name}</p>
                <p className="text-sm text-slate-400">{companyInfo?.name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">有給残日数</p>
                <p className="text-3xl font-bold text-emerald-400">
                  {employee.paid_leave_balance || 0}
                  <span className="text-sm text-slate-400 ml-1">日</span>
                </p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-400">年間付与</p>
                <p className="text-lg font-semibold">{employee.paid_leave_granted || 0}日</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">使用済み</p>
                <p className="text-lg font-semibold">{employee.paid_leave_used || 0}日</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Request Button */}
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="w-full bg-emerald-600 hover:bg-emerald-500 h-12"
          >
            <CalendarDays className="w-5 h-5 mr-2" />
            新規申請
          </Button>
        )}

        {/* Request Form */}
        {showForm && (
          <Card className="bg-slate-800 border-emerald-500/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-400" />
                有給休暇申請
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">開始日</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left mt-1 bg-slate-700 border-slate-600">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {startDate ? format(startDate, 'M/d (E)', { locale: ja }) : '選択'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => {
                          setStartDate(date);
                          if (!endDate || date > endDate) setEndDate(date);
                        }}
                        locale={ja}
                        className="bg-slate-800"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <Label className="text-slate-300">終了日</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left mt-1 bg-slate-700 border-slate-600">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {endDate ? format(endDate, 'M/d (E)', { locale: ja }) : '選択'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => startDate && date < startDate}
                        locale={ja}
                        className="bg-slate-800"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Half Day Option */}
              {startDate && (!endDate || format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) && (
                <div>
                  <Label className="text-slate-300">半休オプション</Label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      variant={halfDay === null ? "default" : "outline"}
                      onClick={() => setHalfDay(null)}
                      className={`flex-1 ${halfDay === null ? 'bg-emerald-600' : 'bg-slate-700 border-slate-600'}`}
                    >
                      全日
                    </Button>
                    <Button
                      variant={halfDay === "am" ? "default" : "outline"}
                      onClick={() => setHalfDay("am")}
                      className={`flex-1 ${halfDay === "am" ? 'bg-emerald-600' : 'bg-slate-700 border-slate-600'}`}
                    >
                      午前半休
                    </Button>
                    <Button
                      variant={halfDay === "pm" ? "default" : "outline"}
                      onClick={() => setHalfDay("pm")}
                      className={`flex-1 ${halfDay === "pm" ? 'bg-emerald-600' : 'bg-slate-700 border-slate-600'}`}
                    >
                      午後半休
                    </Button>
                  </div>
                </div>
              )}

              {/* Days Summary */}
              {startDate && (
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <p className="text-sm text-slate-400">申請日数</p>
                  <p className="text-2xl font-bold text-emerald-400">{calculateDays()}日</p>
                </div>
              )}

              {/* Reason */}
              <div>
                <Label className="text-slate-300">理由（任意）</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="例: 私用のため"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setStartDate(null);
                    setEndDate(null);
                    setHalfDay(null);
                    setReason('');
                  }}
                  className="flex-1 border-slate-600"
                >
                  キャンセル
                </Button>
                <Button
                  onClick={submitRequest}
                  disabled={submitting || !startDate}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />送信中...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />申請する</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Request History */}
        <div>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-400" />
            申請履歴
          </h2>
          
          {requests.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6 text-center text-slate-400">
                <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>申請履歴がありません</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <Card key={req.id} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(req.status)}
                          {req.half_day && (
                            <span className="text-xs text-slate-400">
                              {req.half_day === 'am' ? '午前半休' : '午後半休'}
                            </span>
                          )}
                        </div>
                        <p className="font-medium">
                          {req.start_date === req.end_date 
                            ? format(parseISO(req.start_date), 'M月d日 (E)', { locale: ja })
                            : `${format(parseISO(req.start_date), 'M月d日', { locale: ja })} 〜 ${format(parseISO(req.end_date), 'M月d日', { locale: ja })}`
                          }
                        </p>
                        <p className="text-sm text-slate-400">{req.days}日</p>
                        {req.reason && (
                          <p className="text-sm text-slate-500 mt-1">理由: {req.reason}</p>
                        )}
                        {req.status === 'rejected' && req.rejected_reason && (
                          <p className="text-sm text-red-400 mt-1">却下理由: {req.rejected_reason}</p>
                        )}
                      </div>
                      
                      {req.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelRequest(req.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
